<template>
  <div
    :id="'review-item-' + reviewItem.id"
    class="cursor-pointer sticky left-0 z-40 transition-colors h-full border-b border-neutral-background-default bg-neutral-background-default"
    :class="{
      '!bg-orange-50': reviewItem.state === StudyState.DUPLICATE,
    }"
  >
    <div v-if="hasActiveJobs" class="absolute top-2 left-2 z-10 flex gap-1">
      <div
        v-if="jobStatuses['auto-screen']"
        v-tooltip="
          `Auto-screening: ${jobStatuses['auto-screen'].status === 'active' ? 'in progress' : jobStatuses['auto-screen'].status}${jobStatuses['auto-screen'].error ? '\n' + jobStatuses['auto-screen'].error : ''}`
        "
        class="w-4 h-4 rounded-full border border-white shadow-sm flex items-center justify-center"
        :class="{
          'bg-blue-500 animate-pulse':
            jobStatuses['auto-screen'].status === 'active',
          'bg-yellow-500': jobStatuses['auto-screen'].status === 'waiting',
          'bg-red-500': jobStatuses['auto-screen'].status === 'failed',
        }"
      >
        <span
          v-if="jobStatuses['auto-screen'].status === 'active'"
          class="text-white text-[8px]"
          >S</span
        >
        <span
          v-if="jobStatuses['auto-screen'].status === 'waiting'"
          class="text-white text-[8px]"
          >S</span
        >
        <span
          v-if="jobStatuses['auto-screen'].status === 'failed'"
          class="text-white text-[8px]"
          >!</span
        >
      </div>

      <div
        v-if="jobStatuses['auto-extract']"
        v-tooltip="
          `Auto-extraction: ${jobStatuses['auto-extract'].status === 'active' ? 'in progress' : jobStatuses['auto-extract'].status}${jobStatuses['auto-extract'].error ? '\n' + jobStatuses['auto-extract'].error : ''}`
        "
        class="w-4 h-4 rounded-full border border-white shadow-sm flex items-center justify-center"
        :class="{
          'bg-blue-500 animate-pulse':
            jobStatuses['auto-extract'].status === 'active',
          'bg-yellow-500': jobStatuses['auto-extract'].status === 'waiting',
          'bg-red-500': jobStatuses['auto-extract'].status === 'failed',
        }"
      >
        <span
          v-if="jobStatuses['auto-extract'].status === 'active'"
          class="text-white text-[8px]"
          >E</span
        >
        <span
          v-if="jobStatuses['auto-extract'].status === 'waiting'"
          class="text-white text-[8px]"
          >E</span
        >
        <span
          v-if="jobStatuses['auto-extract'].status === 'failed'"
          class="text-white text-[8px]"
          >!</span
        >
      </div>

      <div
        v-if="jobStatuses['pdf-retrieval']"
        v-tooltip="
          `PDF retrieval: ${jobStatuses['pdf-retrieval'].status === 'active' ? 'in progress' : jobStatuses['pdf-retrieval'].status}${jobStatuses['pdf-retrieval'].error ? '\n' + jobStatuses['pdf-retrieval'].error : ''}`
        "
        class="w-4 h-4 rounded-full border border-white shadow-sm flex items-center justify-center"
        :class="{
          'bg-blue-500 animate-pulse':
            jobStatuses['pdf-retrieval'].status === 'active',
          'bg-yellow-500': jobStatuses['pdf-retrieval'].status === 'waiting',
          'bg-red-500': jobStatuses['pdf-retrieval'].status === 'failed',
        }"
      >
        <span
          v-if="jobStatuses['pdf-retrieval'].status === 'active'"
          class="text-white text-[8px]"
          >P</span
        >
        <span
          v-if="jobStatuses['pdf-retrieval'].status === 'waiting'"
          class="text-white text-[8px]"
          >P</span
        >
        <span
          v-if="jobStatuses['pdf-retrieval'].status === 'failed'"
          class="text-white text-[8px]"
          >!</span
        >
      </div>
    </div>

    <ArticleDetails
      v-if="article"
      :article="article"
      :expansion-panel="expansionPanel"
    />
    <IncidentDetails
      v-else-if="incident"
      :incident="incident"
      :expansion-panel="expansionPanel"
    />
    <GuidanceDetails
      v-else-if="guidance"
      :guidance="guidance"
      :expansion-panel="expansionPanel"
    />
    <RegistrationDetails
      v-else-if="registration"
      :registration="registration"
      :expansion-panel="expansionPanel"
    />
    <FieldSafetyNoticeDetails
      v-else-if="fsn"
      :fsn="fsn"
      :expansion-panel="expansionPanel"
    />
  </div>
</template>
<script setup lang="ts">
import ArticleDetails from './Article.vue'
import IncidentDetails from './Incident.vue'
import GuidanceDetails from './Guidance.vue'

import { ReviewItemType } from '@core/domain/types/reviewItemType.type'
import RegistrationDetails from './Registration.vue'
import FieldSafetyNoticeDetails from './FieldSafetyNotice.vue'
import { ReviewItem } from '@core/domain/models/reviewItem.model'
import { computed } from 'vue'
import { Article } from '@core/domain/models/article.model'
import { Incident } from '@core/domain/models/incident.model'
import { Guidance } from '@core/domain/models/guidance.model'
import { Registration } from '@core/domain/models/registration.model'
import { FieldSafetyNotice } from '@core/domain/models/fieldSafetyNotice.model'
import { StudyState } from '@app/types'
import { injectStrict } from '@app/utils/injectStrict'
import { ReviewKey } from '@app/views/Review/use-review'
import { DevToolboxKey } from '@app/injectionKeys'

const { isCuttingEdge } = injectStrict(DevToolboxKey)

const props = defineProps<{
  reviewItem: ReviewItem
  expansionPanel: any
  isMetadataExpanded: boolean
}>()
const { reviewItemJobStatuses } = injectStrict(ReviewKey)

const jobStatuses = computed(() => {
  return reviewItemJobStatuses.value[props.reviewItem.id] || {}
})

const hasActiveJobs = computed(() => {
  const statuses = jobStatuses.value
  return Object.values(statuses).some((job) => {
    if (!job || job.status === 'completed') return false
    if (job.status === 'failed' && !isCuttingEdge) return false
    return true
  })
})

const article = computed(() => {
  if (props.reviewItem.type === ReviewItemType.Article) {
    return props.reviewItem as Article
  }
  return null
})

const incident = computed(() => {
  if (props.reviewItem.type === ReviewItemType.Incident) {
    return props.reviewItem as Incident
  }
  return null
})

const guidance = computed(() => {
  if (props.reviewItem.type === ReviewItemType.Guidance) {
    return props.reviewItem as Guidance
  }
  return null
})

const registration = computed(() => {
  if (props.reviewItem.type === ReviewItemType.Registration) {
    return props.reviewItem as Registration
  }
  return null
})

const fsn = computed(() => {
  if (props.reviewItem.type === ReviewItemType.FieldSafetyNotice) {
    return props.reviewItem as FieldSafetyNotice
  }
  return null
})
</script>
